// src/views/admin/default/components/DailyTraffic.jsx  -> TodoCard
import React, { useEffect, useState } from "react";
import Card from "components/card";
import {
  CalendarDays,
  Plus,
  GripVertical,
  Trash2,
  ClipboardList,
} from "lucide-react";
import {
  getTodos,
  createTodo,
  updateTodo,
  deleteTodo,
} from "services/hrms.api";
import Modal from "components/ui/Modal";

export default function DailyTraffic() {
  const [todos, setTodos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  // add-modal state
  const [showAddModal, setShowAddModal] = useState(false);
  const [newTitle, setNewTitle] = useState("");
  const [saving, setSaving] = useState(false);
  const [addError, setAddError] = useState("");

  // delete confirmation modal state
  const [deleteTarget, setDeleteTarget] = useState(null); // todo object
  const [deleting, setDeleting] = useState(false);

  // load from API
  useEffect(() => {
    (async () => {
      try {
        setError("");
        setLoading(true);
        const { data } = await getTodos();
        setTodos(data || []);
      } catch (e) {
        console.error(e);
        setError("Failed to load todos.");
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  const toggle = async (id) => {
    const current = todos.find((t) => t._id === id);
    if (!current) return;

    const nextDone = !current.done;

    // optimistic UI
    setTodos((list) =>
      list.map((t) => (t._id === id ? { ...t, done: nextDone } : t))
    );

    try {
      await updateTodo(id, { done: nextDone });
    } catch (e) {
      console.error(e);
      // rollback if failed
      setTodos((list) =>
        list.map((t) => (t._id === id ? { ...t, done: !nextDone } : t))
      );
      alert("Failed to update todo");
    }
  };

  // ---------- ADD TODO MODAL ----------
  const openAddModal = () => {
    setNewTitle("");
    setAddError("");
    setShowAddModal(true);
  };

  const closeAddModal = () => {
    if (saving) return; // prevent closing while saving
    setShowAddModal(false);
    setNewTitle("");
    setAddError("");
  };

  const handleAddTodo = async (e) => {
    e?.preventDefault();
    if (!newTitle.trim()) {
      setAddError("Please enter a task title.");
      return;
    }

    try {
      setSaving(true);
      setAddError("");
      const { data } = await createTodo({ title: newTitle.trim() });
      // prepend new todo
      setTodos((list) => [data, ...list]);
      closeAddModal();
    } catch (e) {
      console.error(e);
      setAddError("Failed to create todo. Please try again.");
    } finally {
      setSaving(false);
    }
  };

  // ---------- DELETE CONFIRMATION MODAL ----------
  const openDeleteModal = (todo) => {
    setDeleteTarget(todo);
  };

  const closeDeleteModal = () => {
    if (deleting) return; // prevent closing while deleting
    setDeleteTarget(null);
  };

  const handleConfirmDelete = async () => {
    if (!deleteTarget) return;
    const id = deleteTarget._id;

    try {
      setDeleting(true);
      await deleteTodo(id);
      // remove from UI
      setTodos((list) => list.filter((t) => t._id !== id));
      closeDeleteModal();
    } catch (e) {
      console.error(e);
      alert("Failed to delete task. Please try again.");
    } finally {
      setDeleting(false);
    }
  };

  return (
    <>
      <Card extra="p-0">
        {/* Header */}
        <div className="flex items-center justify-between border-b border-gray-200 px-5 py-3 dark:border-white/10">
          <h3 className="text-base font-semibold text-navy-700 dark:text-white">
            Todo
          </h3>
          <div className="flex items-center gap-2">
            <button
              className="flex items-center gap-2 rounded-lg border border-gray-300 px-3 py-1.5 text-xs font-medium text-gray-700 hover:bg-gray-50 dark:border-white/10 dark:text-white/80 dark:hover:bg-white/10"
              title="Today"
            >
              <CalendarDays className="h-4 w-4" />
              Today
            </button>
            <button
              onClick={openAddModal}
              className="grid h-7 w-7 place-items-center rounded-full bg-gradient-to-r from-yellow-500 to-red-500 text-white hover:bg-orange-600"
              title="Add task"
            >
              <Plus className="h-4 w-4" />
            </button>
          </div>
        </div>

        {/* Body */}
        <div className="p-4">
          {loading && (
            <div className="text-xs text-gray-500 dark:text-white/60">
              Loading todos…
            </div>
          )}

          {error && !loading && (
            <div className="border-rose-200 bg-rose-50 text-rose-700 dark:text-rose-300 rounded-lg border p-2 text-xs dark:border-white/10 dark:bg-white/5">
              {error}
            </div>
          )}

          {!loading && !error && todos.length === 0 && (
            <div className="flex flex-col items-center justify-center px-4 py-10 text-center">
              <ClipboardList
                className="h-10 w-10 text-gray-300 dark:text-white/20"
                strokeWidth={1.3}
              />

              <h3 className="mt-3 text-sm font-semibold text-gray-700 dark:text-white">
                No tasks yet
              </h3>

              <p className="mt-1 text-xs text-gray-500 dark:text-white/60">
                Your task list is empty.
              </p>
            </div>
          )}

          {!loading && !error && todos.length > 0 && (
            <div className="space-y-3">
              {todos.map((t) => (
                <div
                  key={t._id}
                  className="flex items-center justify-between rounded-xl border border-gray-200 px-3 py-3 dark:border-white/10"
                >
                  <div className="flex items-center gap-3">
                    <GripVertical className="h-4 w-4 text-gray-400" />
                    <input
                      type="checkbox"
                      checked={!!t.done}
                      onChange={() => toggle(t._id)}
                      className="h-4 w-4 rounded border-gray-300 text-brand-500 focus:ring-brand-500"
                    />
                    <span
                      className={`text-sm font-medium ${
                        t.done
                          ? "text-gray-400 line-through"
                          : "text-navy-700 dark:text-white"
                      }`}
                    >
                      {t.title}
                    </span>
                  </div>

                  <button
                    type="button"
                    onClick={() => openDeleteModal(t)}
                    className="hover:bg-rose-50 hover:text-rose-500 flex items-center gap-1 rounded-lg px-2 py-1 text-xs text-gray-400 dark:hover:bg-white/5"
                    title="Delete task"
                  >
                    <Trash2 className="h-4 w-4 " />
                    {/* <span className="hidden sm:inline">Delete</span> */}
                  </button>
                </div>
              ))}
            </div>
          )}
        </div>
      </Card>

      {/* Add Todo Modal */}
      <Modal open={showAddModal} onClose={closeAddModal} title="Add a new task">
        <form onSubmit={handleAddTodo} className="space-y-3">
          <div>
            <label className="mb-1 block text-xs font-medium text-gray-600 dark:text-white/70">
              Task title
            </label>
            <input
              type="text"
              value={newTitle}
              onChange={(e) => {
                setNewTitle(e.target.value);
                if (addError) setAddError("");
              }}
              autoFocus
              className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm text-navy-700 outline-none focus:border-brand-500 focus:ring-1 focus:ring-brand-500 dark:border-white/10 dark:bg-navy-700 dark:text-white"
              placeholder="e.g. Follow up with clients"
            />
          </div>

          {addError && <p className="text-rose-500 text-xs">{addError}</p>}

          <div className="mt-2 flex items-center justify-end gap-2">
            <button
              type="button"
              onClick={closeAddModal}
              disabled={saving}
              className="rounded-lg px-3 py-1.5 text-xs font-medium text-gray-600 hover:bg-gray-100 dark:text-white/70 dark:hover:bg-white/10"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={saving}
              className="rounded-lg bg-gradient-to-r from-yellow-500 to-red-500 px-4 py-1.5 text-xs font-semibold text-white hover:bg-orange-600 disabled:cursor-not-allowed disabled:opacity-70"
            >
              {saving ? "Adding…" : "Add Task"}
            </button>
          </div>
        </form>
      </Modal>

      {/* Delete Confirmation Modal */}
      <Modal
        open={!!deleteTarget}
        onClose={closeDeleteModal}
        title="Delete task?"
      >
        <div className="space-y-4 text-sm text-gray-700 dark:text-white/80">
          <p>
            Are you sure you want to delete this task? This action cannot be
            undone.
          </p>
          {deleteTarget && (
            <div className="rounded-lg bg-gray-50 px-3 py-2 text-xs text-gray-600 dark:bg-white/10 dark:text-white/70">
              {deleteTarget.title}
            </div>
          )}

          <div className="mt-2 flex items-center justify-end gap-2">
            <button
              type="button"
              onClick={closeDeleteModal}
              disabled={deleting}
              className="rounded-lg px-3 py-1.5 text-xs font-medium text-gray-600 hover:bg-gray-100 dark:text-white/70 dark:hover:bg-white/10"
            >
              Cancel
            </button>
            <button
              type="button"
              onClick={handleConfirmDelete}
              disabled={deleting}
              className="rounded-lg bg-gradient-to-r from-yellow-500 to-red-500 px-4 py-1.5 text-xs font-semibold text-white hover:bg-orange-600 disabled:cursor-not-allowed disabled:opacity-70"
            >
              {saving ? "Delete" : "Delete Task"}
            </button>
          </div>
        </div>
      </Modal>
    </>
  );
}
